// Scores the tiered, material-aware stone condition assessment from Gemini
// (api/analyze.js) into a severity score/tier and the legacy single-label
// `stone_condition` value the rest of the app still reads. See Decision 11
// in docs/architecture/decisions.md — weights here are a working draft
// (translated from the qualitative threat matrix in that doc), not yet
// calibrated by a conservator. Shared by every caller of api/analyze.js
// (field tool, StoneQA) so the derivation lives in exactly one place.

const DEGREE = {
  none: 0, not_applicable: 0, cannot_determine: 0,
  light: 1, hairline: 1, slight_lean: 1, minor: 1,
  moderate: 2, significant_lean: 2, major: 2,
  heavy: 3, structural: 3, fallen_displaced: 3, total: 3,
}
const degree = v => DEGREE[v] ?? 0

// Marble/sandstone-relative threat weight for biological growth by colour,
// translated from Decision 11's qualitative matrix (Low-Medium=1,
// Medium-High=2, High-Critical/Critical=3). Provisional until Rob/Edna give
// real numbers.
const GROWTH_WEIGHT = {
  marble:    { green: 1, black: 3, red_yellow: 3 },
  sandstone: { green: 2, black: 3, red_yellow: 2 },
  granite:   { green: 1, black: 1, red_yellow: 1 },
  metal:     { green: 1, black: 1, red_yellow: 1 },
  unknown:   { green: 1.5, black: 2, red_yellow: 2 },
}

const TIER_TO_LEGACY_CONDITION = { low: 'good', guarded: 'good', elevated: 'fair', high: 'poor', severe: 'poor' }

// Alert-style display colours per tier, shared by any UI showing severity
// (review screens today; the map in Phase 2). Dark-theme friendly (bg/text
// pairs, not raw hex-on-white).
export const TIER_DISPLAY = {
  low:      { label: 'Low',      bg: '#14532d', text: '#4ade80' },
  guarded:  { label: 'Guarded',  bg: '#1e3a5f', text: '#60a5fa' },
  elevated: { label: 'Elevated', bg: '#78350f', text: '#facc15' },
  high:     { label: 'High',     bg: '#7c2d12', text: '#fb923c' },
  severe:   { label: 'Severe',   bg: '#7f1d1d', text: '#f87171' },
}

// raw = structural*4 + damage*3 + growth(weighted) + legibility*1; normalized 0-100
const MAX_RAW = 3 * 4 + 3 * 3 + 3 * 3 + 3 * 1 // 33

export function scoreConditionAssessment(ca) {
  if (!ca) return null

  const material = ca.material || 'unknown'
  const weights = GROWTH_WEIGHT[material] || GROWTH_WEIGHT.unknown

  const structural = degree(ca.structural_stability)

  const damage = ca.physical_damage || {}
  const damageScore = Math.max(
    degree(damage.cracking),
    degree(damage.spalling_flaking),
    degree(damage.blistering),
    degree(damage.granular_disintegration),
    degree(damage.delamination_shaling),
  )

  const growth = ca.biological_growth || {}
  const growthScore = Math.max(
    degree(growth.green) * weights.green,
    degree(growth.black) * weights.black,
    degree(growth.red_yellow) * weights.red_yellow,
  )

  const legibility = degree(ca.legibility_loss)

  const raw = structural * 4 + damageScore * 3 + growthScore + legibility
  const severityScore = Math.round(Math.min(100, (raw / MAX_RAW) * 100))

  const tier =
    severityScore >= 80 ? 'severe' :
    severityScore >= 60 ? 'high' :
    severityScore >= 40 ? 'elevated' :
    severityScore >= 20 ? 'guarded' : 'low'

  // Hard safety override, independent of the score — black/red/yellow growth
  // means chemical treatment only, never abrasive cleaning (Decision 11).
  const treatmentFlag = (degree(growth.black) > 0 || degree(growth.red_yellow) > 0) ? 'chemical_only' : null

  let legacyCondition = TIER_TO_LEGACY_CONDITION[tier] || 'fair'
  if (legibility >= 3) legacyCondition = 'illegible' // total legibility loss overrides the severity tier for the legacy label

  return { material, severityScore, tier, treatmentFlag, legacyCondition, structural, damageScore, growthScore, legibility }
}

const cap = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : s
const humanize = v => (v || '').replace(/_/g, ' ')

// Renders the assessment as readable text for the legacy `condition_notes`
// free-text field, so today's UI (PersonView, Search, StoneQA) shows the
// richer assessment immediately without needing new columns — see Decision
// 11, Phase 1. Phase 2 persists the structured fields directly.
export function summarizeConditionAssessment(ca) {
  if (!ca) return ''
  const parts = []

  if (ca.material && ca.material !== 'unknown' && ca.material !== 'cannot_determine') {
    parts.push(`Material: ${cap(ca.material)}.`)
  }
  if (ca.structural_stability && ca.structural_stability !== 'upright') {
    parts.push(`Stability: ${humanize(ca.structural_stability)}.`)
  }
  if (ca.prior_repairs) parts.push('Prior repairs visible (cement fill or pins).')

  const growth = ca.biological_growth || {}
  const growthBits = []
  if (degree(growth.green) > 0) growthBits.push(`green/algae (${humanize(growth.green)})`)
  if (degree(growth.black) > 0) growthBits.push(`black growth (${humanize(growth.black)})`)
  if (degree(growth.red_yellow) > 0) growthBits.push(`red/yellow lichen (${humanize(growth.red_yellow)})`)
  if (growthBits.length) parts.push(`Biological growth: ${growthBits.join(', ')}.`)

  const damage = ca.physical_damage || {}
  const damageBits = []
  if (degree(damage.cracking) > 0) damageBits.push(`cracking (${humanize(damage.cracking)})`)
  if (degree(damage.spalling_flaking) > 0) damageBits.push(`spalling/flaking (${humanize(damage.spalling_flaking)})`)
  if (degree(damage.blistering) > 0) damageBits.push(`blistering (${humanize(damage.blistering)})`)
  if (degree(damage.granular_disintegration) > 0) damageBits.push(`granular disintegration/sugaring (${humanize(damage.granular_disintegration)})`)
  if (degree(damage.delamination_shaling) > 0) damageBits.push(`delamination (${humanize(damage.delamination_shaling)})`)
  if (damageBits.length) parts.push(`Physical damage: ${damageBits.join(', ')}.`)

  if (degree(growth.black) > 0 || degree(growth.red_yellow) > 0) {
    parts.push('DO NOT SCRUB — chemical treatment only (black/red/yellow growth present).')
  }
  if (ca.notes) parts.push(ca.notes)

  return parts.join(' ')
}

// Flattens the raw assessment + computed severity into the `stones` table's
// Phase 2 column shape (see Decision 11's migration). Used by every save path
// (field tool, StoneQA) so the mapping lives in exactly one place.
export function toStoneColumns(ca, severity) {
  if (!ca) return {}
  const damage = ca.physical_damage || {}
  const growth = ca.biological_growth || {}
  return {
    material: ca.material || null,
    structural_stability: ca.structural_stability || null,
    prior_repairs: ca.prior_repairs ?? null,
    legibility_loss: ca.legibility_loss || null,
    growth_green: growth.green || null,
    growth_black: growth.black || null,
    growth_red_yellow: growth.red_yellow || null,
    damage_cracking: damage.cracking || null,
    damage_spalling_flaking: damage.spalling_flaking || null,
    damage_blistering: damage.blistering || null,
    damage_granular_disintegration: damage.granular_disintegration || null,
    damage_delamination_shaling: damage.delamination_shaling || null,
    severity_score: severity?.severityScore ?? null,
    severity_tier: severity?.tier ?? null,
    treatment_flag: severity?.treatmentFlag ?? null,
  }
}
